import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, LoadingOverlay, Modal, TextInput, Title } from "@mantine/core";
import { handleError } from "src/utils/error";

import { useClientTenant } from "../../hooks/clients";
import { createModal } from "../../hooks/modals";
import { useUpdateTenantName } from "../../hooks/tenants";

export interface UpdateTenantNameModalProps {
    tenantId: string;
    onClose();
    onSave?();
}

export const UpdateTenantNameModal = createModal(({ tenantId, onClose, onSave }: UpdateTenantNameModalProps) => {
    const { t } = useTranslation(["common"]);

    const [name, setName] = useState<string>();

    const { clientTenant, isLoadingClientTenant } = useClientTenant(tenantId);

    useEffect(() => {
        setName(clientTenant?.name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientTenant?.name]);

    const { updateTenantName, isUpdatingTenantName } = useUpdateTenantName(tenantId);

    const handleSubmit = useCallback(async () => {
        try {
            await updateTenantName(name);

            onSave?.();
            onClose?.();
        } catch (err) {
            handleError(err, { context: { tenantId } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, onSave, onClose]);

    return (
        <Modal size="md" title={<Title order={2}>{t("tenant_name_update")}</Title>} onClose={onClose} opened>
            <LoadingOverlay visible={isLoadingClientTenant || isUpdatingTenantName} />
            <TextInput
                mb="md"
                type="text"
                label={t("tenant_name")}
                placeholder={t("tenant_name")}
                value={name}
                onChange={(event) => setName(event?.target?.value)}
                withAsterisk
            />
            <Button disabled={!name} onClick={() => handleSubmit()}>
                {t("save")}
            </Button>
        </Modal>
    );
});
